import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import empManagementService from '../services/empManagementService';

/**
 * Shared "Select UDA" options for the Employee Management setup master screens.
 *
 * Mirrors the Add Employee form (EmployeeDetailsTab): the dropdown is sourced from
 * the UDA master (ds_dtcp.ds_uda_lst_t via empManagementService.getAllUDAs) instead
 * of the raw auth-context tenant list. Each row is normalized to the dropdown's
 * { code, name } shape where:
 *   - code = the operational tenant code ('10001'-'10021'), kept as the persisted
 *            selection value so every tenant-based backend path is unchanged;
 *   - name = uda_nm (the displayed UDA label); uda_id/uda_cd are carried for reference.
 *
 * The list is PRE-FILTERED to the logged-in user's jurisdiction (authUser.tenants).
 * It falls back to the full UDA list ONLY when the user has no jurisdiction tenants
 * configured (admin/test accounts), preserving the prior behavior.
 *
 * Only state/regional users get a populated list; for tenant-scoped users the setup
 * screens render their own (disabled) tenant field, so an empty list is returned.
 */
export function useUdaOptions(): { ulbs: any[]; loadingUlbs: boolean } {
  const { user: authUser } = useAuth();
  const isStateUser = (authUser as any)?.isStateRegionalUser || false;
  const authTenants = (authUser as any)?.tenants || [];
  // Stable key of the user's authorized tenant codes — drives both the fetch effect
  // and the jurisdiction filter (avoids re-running on every render).
  const allowedCodesKey = authTenants
    .map((t: any) => String(t?.code ?? t?.tenant_code ?? ''))
    .filter(Boolean)
    .sort()
    .join(',');

  const [fetchedUlbs, setFetchedUlbs] = useState<any[]>([]);
  const [loadingUlbs, setLoadingUlbs] = useState(false);

  useEffect(() => {
    if (!isStateUser) return;
    let cancelled = false;
    setLoadingUlbs(true);
    const allowed = new Set(allowedCodesKey ? allowedCodesKey.split(',') : []);
    empManagementService.getAllUDAs()
      .then((list: any[]) => {
        if (cancelled) return;
        const opts = (list || [])
          .filter((u: any) => u?.tenant_code != null && String(u.tenant_code) !== '')
          .map((u: any) => ({ code: String(u.tenant_code), name: u.uda_nm, uda_id: u.uda_id, uda_cd: u.uda_cd }));
        const filtered = allowed.size > 0 ? opts.filter((o: any) => allowed.has(o.code)) : opts;
        setFetchedUlbs(filtered);
      })
      .catch(() => { if (!cancelled) setFetchedUlbs([]); })
      .finally(() => { if (!cancelled) setLoadingUlbs(false); });
    return () => { cancelled = true; };
  }, [isStateUser, allowedCodesKey]);

  return { ulbs: isStateUser ? fetchedUlbs : [], loadingUlbs };
}
